import React from 'react';
import { motion } from 'framer-motion';
import { Award, Users, MapPin, Gem, Clock } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useTranslation } from '../data/translations';

export const TrustSignals = () => {
  const { language } = useLanguage();
  const t = useTranslation(language);

  const signals = [
    { icon: Award, title: t.trust.experience, description: t.trust.experience_desc, color: '#E31C23' },
    { icon: Users, title: t.trust.apprentice, description: t.trust.apprentice_desc, color: '#00A651' },
    { icon: MapPin, title: t.trust.locations, description: t.trust.locations_desc, color: '#E31C23' },
    { icon: Gem, title: t.trust.materials, description: t.trust.materials_desc, color: '#C41230' },
    { icon: Clock, title: t.trust.reliable, description: t.trust.reliable_desc, color: '#00A651' }
  ];

  return (
    <section className="py-20 md:py-28 bg-[#F8F9FA]" data-testid="trust-signals-section">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#1A1A1A] mb-4" data-testid="trust-title">{t.trust.title}</h2>
          <div className="w-20 h-1 bg-[#E31C23] mx-auto" />
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
          {signals.map((signal, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="group bg-white p-6 border border-gray-100 hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
              data-testid={`trust-signal-${index}`}
            >
              <div className="w-14 h-14 flex items-center justify-center mb-4" style={{ backgroundColor: `${signal.color}15` }}>
                <signal.icon className="w-7 h-7" style={{ color: signal.color }} />
              </div>
              <h3 className="font-heading text-lg font-semibold text-[#1A1A1A] mb-2">{signal.title}</h3>
              <p className="text-sm text-gray-600 leading-relaxed">{signal.description}</p>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-8"
        >
          {[
            { number: '30+', label: t.hero.years_label },
            { number: '2', label: t.hero.locations_stat },
            { number: '1000+', label: t.hero.projects_stat },
            { number: '100%', label: t.references.satisfaction }
          ].map((stat, index) => (
            <div key={index} className="text-center" data-testid={`stat-${index}`}>
              <div className="font-heading text-4xl md:text-5xl font-bold text-[#E31C23] mb-2">{stat.number}</div>
              <div className="text-sm text-gray-600 font-medium uppercase tracking-wider">{stat.label}</div>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};
